// src/schema/aiInterviewSchema.js
// === Canonical Schema for AI Interview (LT完全無視) ====================
export const QIDS = [
  "msws1", "msws2", "msws3",          // 歩行
  "mfis_cog", "mfis_phy", "mfis_soc", // 疲労
  "msnq_att", "msnq_mem", "msnq_other", "msnq_pb", // 高次脳
  "amsq",                              // 上肢
];

// UI表示ラベル
export const LABELS = {
  msws1: "歩行機能1",
  msws2: "歩行機能2",
  msws3: "歩行機能3",
  mfis_cog: "疲労（認知）",
  mfis_phy: "疲労（身体動作）",
  mfis_soc: "疲労（社会性）",
  msnq_att: "高次脳機能（情報処理速度）",
  msnq_mem: "高次脳機能（記憶）",
  msnq_other: "高次脳機能（その他活動）",
  msnq_pb: "高次脳機能（人格・行動）",
  amsq: "上肢機能",
};

// 大グループ
export const FUNCTION_GROUPS = {
  "歩行機能": ["msws1", "msws2", "msws3"],
  "上肢機能": ["amsq"],
  "疲労": ["mfis_cog", "mfis_phy", "mfis_soc"],
  "高次脳機能": ["msnq_att", "msnq_mem", "msnq_other", "msnq_pb"],
};

export const GROUP_ORDER = ["歩行機能", "上肢機能", "疲労", "高次脳機能"];
export const KEY_ORDER = {
  "歩行機能": ["msws1", "msws2", "msws3", "walking_average"],
  "上肢機能": ["amsq"],
  "疲労": ["mfis_cog", "mfis_phy", "mfis_soc"],
  "高次脳機能": ["msnq_att", "msnq_mem", "msnq_other", "msnq_pb"],
};

// 正準フィールド名
export const f = (qid, suffix) => `ai_interview_${qid}_${suffix}`;
export const FIELD = {
  date: "ai_answer_date",
  question: (qid) => f(qid, "question"),
  answer:   (qid) => f(qid, "answer"),
  regen:    (qid) => f(qid, "regen"),
  sub:      (qid) => f(qid, "sub"),
};

// 互換: *_score を数値回答として吸収
const LEGACY_ALT = (qid, rec) => rec?.[`ai_interview_${qid}_score`];

// ---- アクセサ ---------------------------------------------------------
export const getDate = (rec) => rec?.[FIELD.date] ?? null;

export const getQuestionText = (rec, qid) => {
  if (!rec) return "";
  const primary = rec?.[FIELD.question(qid)];
  if (typeof primary === "string" && primary.length) return primary;
  const alts = [
    `ai_interview_${qid}_ai_question`,
    `ai_interview_${qid}_question_text`,
    `ai_interview_${qid}_q`,
    `ai_interview_${qid}_text`,
  ];
  for (const k of alts) {
    const v = rec?.[k];
    if (typeof v === "string" && v.length) return v;
  }
  return "";
};

// *_sub と *_sub_id 両対応
export const getSubId = (rec, qid) => {
  const v = rec?.[FIELD.sub(qid)];
  if (Number.isFinite(Number(v))) return Math.round(Number(v));
  const alt = rec?.[`${FIELD.sub(qid)}_id`];
  return Number.isFinite(Number(alt)) ? Math.round(Number(alt)) : undefined;
};

export const getRegenCount = (rec, qid) => {
  const v = Number(rec?.[FIELD.regen(qid)]);
  return Number.isFinite(v) ? Math.round(v) : 0;
};

export const getAnswer = (rec, qid) => {
  const v = rec?.[FIELD.answer(qid)];
  if (isFiniteNumber(v)) return Number(v);
  const legacy = LEGACY_ALT(qid, rec);
  return isFiniteNumber(legacy) ? Number(legacy) : undefined;
};

// 表示ラベル・キー順
export const getLabel = (qid) => LABELS[qid] ?? qid;
export const keyToGroup = (qid) => {
  for (const [g, ids] of Object.entries(FUNCTION_GROUPS)) {
    if (ids.includes(qid)) return g;
  }
  return "高次脳機能";
};

// ==== question.json マッピング（LT無し・完全一致のみ） ===============
const CATEGORY_MAP = {
  msws1: ["1MSWS"],
  msws2: ["2MSWS"],
  msws3: ["3MSWS"],
  amsq:  ["AMSQ"],
  mfis_cog:  ["MFIS_COG"],
  mfis_phy:  ["MFIS_PHY"],
  mfis_soc:  ["MFIS_SOC"],
  msnq_att:  ["MSNQ_ATT"],
  msnq_mem:  ["MSNQ_MEM"],
  msnq_other:["MSNQ_OTHER"],
  msnq_pb:   ["MSNQ_PB"],
};

export function pickBaseQuestion(qid, subId, questionBank = []) {
  if (subId == null) return "";
  const cats = CATEGORY_MAP[qid] || [];
  const subStr = String(subId);
  for (const cat of cats) {
    const hit = questionBank.find(
      (q) => q?.category_id === cat && String(q?.sub_id) === subStr
    );
    if (hit?.base_question) return hit.base_question;
  }
  return "";
}

// ---- ユーティリティ ---------------------------------------------------
const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);
export const avg = (arr) => arr.reduce((s, v) => s + v, 0) / arr.length;
export const round2 = (n) => Math.round(n * 100) / 100;

// records -> series（timestamp, qidごとの値, 歩行平均は任意で付与）
export function buildSeries(records, qids, { includeWalkingAverage = false } = {}) {
  return (records ?? []).map((rec) => {
    const row = { timestamp: new Date(getDate(rec) ?? Date.now()).getTime() };
    (qids ?? []).forEach((qid) => {
      const v = getAnswer(rec, qid);
      if (typeof v === "number" && Number.isFinite(v)) row[qid] = Number(v);
    });
    if (includeWalkingAverage) {
      const walkingIds = FUNCTION_GROUPS["歩行機能"] ?? [];
      const wVals = walkingIds
        .map((id) => getAnswer(rec, id))
        .filter((v) => typeof v === "number" && Number.isFinite(v));
      if (wVals.length === walkingIds.length) {
        row["walking_average"] = round2(avg(wVals));
      }
    }
    return row;
  });
}

// DataTable用：qid配列から行キーを作る（歩行は平均だけを行に出す）
export function orderedRowKeysFromQids(qids, { includeWalkingAverage = true } = {}) {
  const uniq = new Set(qids ?? []);
  const out = [];
  const walking = ["msws1", "msws2", "msws3"];
  if (includeWalkingAverage && walking.some((id) => uniq.has(id))) out.push("walking_average");
  if (uniq.has("amsq")) out.push("amsq");
  ["mfis_cog", "mfis_phy", "mfis_soc"].forEach((id) => uniq.has(id) && out.push(id));
  ["msnq_att", "msnq_mem", "msnq_other", "msnq_pb"].forEach((id) => uniq.has(id) && out.push(id));
  return out;
}

// 折れ線キー順（Legend順も同じ）：歩行グループ指定時は個別線を除外
export function orderedLineKeysFromGroups(selectedGroups, allIncludedQids) {
  const walking = FUNCTION_GROUPS["歩行機能"] ?? [];
  const base = (allIncludedQids ?? []).filter(
    (qid) => !(selectedGroups?.includes("歩行機能") && walking.includes(qid))
  );
  return base.sort((a, b) => {
    const ga = keyToGroup(a), gb = keyToGroup(b);
    const gi = GROUP_ORDER.indexOf(ga), gj = GROUP_ORDER.indexOf(gb);
    if (gi !== gj) return gi - gj;
    return (KEY_ORDER[ga]?.indexOf(a) ?? 0) - (KEY_ORDER[gb]?.indexOf(b) ?? 0);
  });
}